import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { brand } from '../brand/brand';
import { useAuth } from '../auth/AuthContext';

/**
 * The header from Direction B: a floating rounded card over the page background
 * rather than a full-bleed bar.
 *
 * NO CITY in the search pill. The design mockup had a fixed city label there,
 * but there is no location service, no city list, and MarketplaceService has no
 * notion of place — so a city could only ever be a hard-coded guess, and the
 * mockup's was the wrong country. The pill is a search box and nothing else
 * until location is real.
 *
 * SEARCH submits to /?q=… and the homepage passes the term to the API, which
 * genuinely supports it. Category and "near me" filtering do not exist
 * server-side yet.
 */
export function Header() {
  const { userId, signOut } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const active = params.get('q') ?? '';
  const [term, setTerm] = useState(active);

  /*
   * KEEPS THE BOX SHOWING WHAT IS ACTUALLY BEING SEARCHED.
   *
   * Without this, opening a shared /?q=dinner link, or pressing back after a
   * search, leaves the field empty while the results below say "Results for
   * dinner" — so the customer cannot edit their own search, only retype it.
   *
   * Keyed on `active`, not on every render, so typing is never overwritten
   * mid-word.
   */
  useEffect(() => { setTerm(active); }, [active]);

  return (
    <header className="site-header">
      <div className="inner">
        {/* The name comes from brand/brand.json — never typed here. */}
        <Link to="/" className="logo" aria-label={`${brand.name} home`}>
          <span className="logo-tile" aria-hidden="true">
            <span className="logo-dot" />
          </span>
          <span className="wordmark">{brand.name}</span>
        </Link>

        <form
          className="search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            const q = term.trim();
            navigate(q === '' ? '/' : `/?q=${encodeURIComponent(q)}`);
          }}
        >
          <svg className="search-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <circle cx="11" cy="11" r="6.5" />
            <path d="M16 16l4 4" />
          </svg>

          <label htmlFor="q" className="visually-hidden">Search deals</label>
          <input
            id="q"
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Search deals, places, categories…"
          />

          <button type="submit">Search</button>
        </form>

        <nav aria-label="Account" className="stack-h" style={{ gap: 10 }}>
          {userId === null ? (
            <Link to="/signin" className="btn">Sign in</Link>
          ) : (
            <>
              {/* Points at the dashboard rather than /vouchers: the vouchers tab is
                  its default view, so nothing is further away, and orders and
                  account details are now reachable in one click instead of none. */}
              <Link to="/account" className="chip-btn">
                <span className="chip-avatar" aria-hidden="true" />
                My account
              </Link>
              <button type="button" className="chip-btn" onClick={() => void signOut()}>
                Sign out
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
